import { dictionaryApi } from './dictionaryClient';

export type BhedBikalpa = {
  id: number;
  bhedId: number;
  bikalpaOrder: number;
  text: string;
};

export type CreateBhedBikalpaBody = {
  bhedId: number;
  bikalpa_order: number;
  text: string;
};

export type UpdateBhedBikalpaBody = Partial<Pick<CreateBhedBikalpaBody, 'bikalpa_order' | 'text'>>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

export function normalizeBhedBikalpa(raw: unknown): BhedBikalpa | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  const bhedId = toNum(raw.bhedId ?? raw.bhed_id);
  const order = toNum(raw.bikalpaOrder ?? raw.bikalpa_order);
  if (id == null || bhedId == null || order == null) return null;
  return {
    id,
    bhedId,
    bikalpaOrder: order,
    text: toStr(raw.text),
  };
}

function normalizeList(data: unknown): BhedBikalpa[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeBhedBikalpa).filter((x): x is BhedBikalpa => x != null);
}

export async function listBhedBikalpa(): Promise<BhedBikalpa[]> {
  const res = await dictionaryApi.get<unknown>('/bhed-bikalpa');
  return normalizeList(res.data);
}

export async function getBhedBikalpa(id: number): Promise<BhedBikalpa> {
  const res = await dictionaryApi.get<unknown>(`/bhed-bikalpa/${id}`);
  const row = normalizeBhedBikalpa(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed-bikalpa response');
  return row;
}

export async function createBhedBikalpa(body: CreateBhedBikalpaBody): Promise<BhedBikalpa> {
  const res = await dictionaryApi.post<unknown>('/bhed-bikalpa', body);
  const row = normalizeBhedBikalpa(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed-bikalpa response');
  return row;
}

export async function updateBhedBikalpa(id: number, body: UpdateBhedBikalpaBody): Promise<BhedBikalpa> {
  const res = await dictionaryApi.patch<unknown>(`/bhed-bikalpa/${id}`, body);
  const row = normalizeBhedBikalpa(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed-bikalpa response');
  return row;
}

export async function deleteBhedBikalpa(id: number): Promise<void> {
  await dictionaryApi.delete(`/bhed-bikalpa/${id}`);
}
